import {
  CloudRain,
  Download,
  Gauge,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Save,
  Upload,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import { useRef, useState } from 'react';
import { exportSave, importSave, manualSave } from '../save/gameSave';
import { useGameStore } from '../simulation/store';
import { formatClock } from '../simulation/systems/time';
import { weatherLabel } from '../simulation/systems/weather';
import { IconButton } from './IconButton';

export function HUD() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [saveStatus, setSaveStatus] = useState('Saved');
  const time = useGameStore((state) => state.time);
  const economy = useGameStore((state) => state.economy);
  const progression = useGameStore((state) => state.progression);
  const weather = useGameStore((state) => state.weather);
  const population = useGameStore((state) => state.world.citizens.length);
  const togglePaused = useGameStore((state) => state.togglePaused);
  const setSpeed = useGameStore((state) => state.setSpeed);
  const rotateCamera = useGameStore((state) => state.rotateCamera);
  const zoomCamera = useGameStore((state) => state.zoomCamera);

  const handleManualSave = async () => {
    setSaveStatus('Saving');
    await manualSave();
    setSaveStatus('Saved');
  };

  return (
    <header className="pointer-events-none absolute left-0 right-0 top-0 z-30 p-3 md:p-4">
      <div className="glass-panel pointer-events-auto mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 rounded-lg px-3 py-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <div className="rounded-md bg-hive-ink px-3 py-2 font-display text-sm font-bold text-hive-cream md:text-base">
            The Bee&apos;s Hive
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            <span className="rounded-md bg-white/55 px-2.5 py-1">Day {time.day}</span>
            <span className="rounded-md bg-white/55 px-2.5 py-1">{formatClock(time.minuteOfDay)}</span>
            <span className="rounded-md bg-white/55 px-2.5 py-1">{progression.milestone}</span>
            <span className="rounded-md bg-white/55 px-2.5 py-1">{population} residents</span>
            <span className="rounded-md bg-white/55 px-2.5 py-1">{Math.floor(economy.credits)} credits</span>
            <span className="inline-flex items-center gap-1 rounded-md bg-white/55 px-2.5 py-1">
              <CloudRain size={15} /> {weatherLabel(weather.kind)}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <IconButton label={time.paused ? 'Resume' : 'Pause'} onClick={togglePaused}>
            {time.paused ? <Play size={18} /> : <Pause size={18} />}
          </IconButton>
          <label className="flex items-center gap-2 rounded-md border border-hive-ink/15 bg-hive-cream/80 px-2 py-1.5 text-sm font-semibold">
            <Gauge size={16} />
            <select
              className="bg-transparent outline-none"
              value={time.speed}
              onChange={(event) => setSpeed(Number(event.target.value) as 1 | 2 | 4)}
            >
              <option value={1}>1x</option>
              <option value={2}>2x</option>
              <option value={4}>4x</option>
            </select>
          </label>
          <IconButton label="Rotate left" onClick={() => rotateCamera(0.16)}>
            <RotateCcw size={18} />
          </IconButton>
          <IconButton label="Rotate right" onClick={() => rotateCamera(-0.16)}>
            <RotateCw size={18} />
          </IconButton>
          <IconButton label="Zoom in" onClick={() => zoomCamera(3)}>
            <ZoomIn size={18} />
          </IconButton>
          <IconButton label="Zoom out" onClick={() => zoomCamera(-3)}>
            <ZoomOut size={18} />
          </IconButton>
          <IconButton label={saveStatus} onClick={handleManualSave}>
            <Save size={18} />
          </IconButton>
          <IconButton label="Export save" onClick={exportSave}>
            <Download size={18} />
          </IconButton>
          <IconButton label="Import save" onClick={() => inputRef.current?.click()}>
            <Upload size={18} />
          </IconButton>
          <input
            ref={inputRef}
            className="hidden"
            type="file"
            accept="application/json"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                void importSave(file);
              }
            }}
          />
        </div>
      </div>
    </header>
  );
}
